import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * OffersQuickAccess — Apple-style 3-capsule quick access to the 3 offers.
 *   1. Ebook (47€)         → /offre
 *   2. Coaching PRSNL      → /prsnl
 *   3. App OS              → /app
 *
 * Hides the current-page card by default to avoid self-reference (e.g. on /prsnl we hide the PRSNL card).
 *
 * Variant prop:
 *   - 'light' — white-ish bg pill (for light pages like home / results)
 *   - 'dark'  — dark pill (for dark sections)
 */

const OFFERS = [
  {
    id: 'sabril-express',
    href: '/prsnl#offres',
    label: 'Sabril Express',
    sub: '790€',
    badge: 'RAPIDE',
    badgeColor: '#22C55E',
    icon: '⚡',
    gradient: 'linear-gradient(180deg, #22C55E 0%, #16A34A 100%)',
    glow: 'rgba(34,197,94,0.45)',
  },
  {
    id: 'coaching',
    href: '/configurer',
    label: 'Salon Privé',
    sub: '90 jours',
    badge: '★',
    badgeColor: '#FBBF24',
    icon: '🤝',
    gradient: 'linear-gradient(180deg, #F05A1A 0%, #DC2626 100%)',
    glow: 'rgba(240,90,26,0.55)',
  },
  {
    id: 'app',
    href: '/app',
    label: 'App OS',
    sub: 'Audit Resto',
    badge: 'NEW',
    badgeColor: '#EF4444',
    icon: '⚙️',
    gradient: 'linear-gradient(180deg, #FBBF24 0%, #F59E0B 100%)',
    glow: 'rgba(251,191,36,0.45)',
  },
];

const OffersQuickAccess = ({ variant = 'light', kicker = '🎯 Accès rapide', title, hideCurrentPage = true, className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const visibleOffers = OFFERS.filter((o) => {
    if (!hideCurrentPage) return true;
    return !pathname.startsWith(o.href);
  });

  const isDark = variant === 'dark';

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5 }}
      className={`my-6 rounded-3xl overflow-hidden relative ${className}`}
      data-testid="offers-quick-access"
      style={{
        background: isDark
          ? 'linear-gradient(180deg, #0F0F12 0%, #1A0E0E 100%)'
          : 'linear-gradient(180deg, #FAF7F2 0%, #F5EFE5 100%)',
        boxShadow: isDark
          ? '0 16px 40px rgba(0,0,0,0.45)'
          : '0 16px 40px rgba(0,0,0,0.06)',
        border: isDark
          ? '1px solid rgba(255,255,255,0.06)'
          : '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <div className="px-5 sm:px-7 py-5 sm:py-6">
        {/* Kicker + title */}
        <p
          className="text-[10px] font-black tracking-[3px] uppercase mb-1"
          style={{ color: isDark ? '#F05A1A' : '#F05A1A' }}
        >
          {kicker}
        </p>
        {title && (
          <h3
            className="font-bebas leading-[0.95] tracking-tight mb-4"
            style={{ color: isDark ? '#FFFFFF' : '#1A1A1A', fontSize: 'clamp(22px, 5vw, 30px)' }}
          >
            {title}
          </h3>
        )}

        {/* iOS-style app icon row — compact squircles, not full-width pills */}
        <div className="flex items-start justify-center flex-wrap gap-5 sm:gap-7 mt-2">
          {visibleOffers.map((o, i) => (
            <motion.button
              key={o.id}
              type="button"
              onClick={() => {
                if (o.href.includes('#')) {
                  const [path, hash] = o.href.split('#');
                  navigate(path);
                  setTimeout(() => {
                    const el = document.getElementById(hash);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 300);
                } else {
                  navigate(o.href);
                }
              }}
              initial={{ opacity: 0, y: 14, scale: 0.92 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, type: 'spring', stiffness: 220, damping: 18 }}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.94 }}
              className="flex flex-col items-center cursor-pointer group w-[78px] sm:w-[96px]"
              data-testid={`offer-quick-${o.id}`}
              aria-label={`Aller à ${o.label}`}
            >
              {/* iOS app icon squircle */}
              <div
                className="relative w-[68px] h-[68px] sm:w-[84px] sm:h-[84px] flex items-center justify-center transition-shadow"
                style={{
                  // iOS 13+ squircle approximation
                  borderRadius: '22.5%',
                  background: o.gradient,
                  boxShadow: `
                    0 10px 24px ${o.glow},
                    inset 0 -3px 8px rgba(0,0,0,0.18),
                    inset 0 2px 4px rgba(255,255,255,0.30),
                    inset 0 1px 0 rgba(255,255,255,0.55)
                  `,
                }}
              >
                {/* Subtle top-gloss highlight (iOS icon shimmer) */}
                <span
                  className="absolute top-[6%] left-[8%] right-[8%] h-[40%] pointer-events-none"
                  style={{
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 100%)',
                    borderRadius: '50% 50% 40% 40% / 70% 70% 30% 30%',
                  }}
                />
                {/* Icon centered */}
                <span
                  className="relative z-[1]"
                  style={{
                    fontSize: 'clamp(28px, 6.8vw, 38px)',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.30))',
                  }}
                >
                  {o.icon}
                </span>

                {/* iOS notification badge */}
                <motion.span
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.08, type: 'spring', stiffness: 380, damping: 16 }}
                  className="absolute -top-1 -right-1 px-1.5 min-w-[20px] h-[20px] rounded-full text-white text-[9px] font-black flex items-center justify-center"
                  style={{
                    background: o.badgeColor,
                    boxShadow: `0 0 0 2px ${isDark ? '#0F0F12' : '#FAF7F2'}, 0 3px 8px ${o.glow}`,
                  }}
                >
                  {o.badge}
                </motion.span>
              </div>

              {/* Label under icon (iOS style) */}
              <p
                className="mt-2 font-bold text-[12px] sm:text-[13px] leading-tight text-center transition-colors"
                style={{ color: isDark ? '#FFFFFF' : '#1A1A1A' }}
              >
                {o.label}
              </p>
              <p
                className="text-[9px] font-bold uppercase tracking-wider text-center mt-0.5"
                style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(26,26,26,0.45)' }}
              >
                {o.sub}
              </p>
            </motion.button>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default OffersQuickAccess;
